import React, { useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { parseTableHtml, serializeTableHtml } from "../articleApi";

const tableFormatters = new Map();

export const applyTableFormat = (blockId, tag) => {
  tableFormatters.get(blockId)?.(tag);
};

const renameInlineTags = (root, from, to) => {
  [...root.querySelectorAll(from)].forEach((node) => {
    const next = document.createElement(to);
    while (node.firstChild) next.appendChild(node.firstChild);
    node.replaceWith(next);
  });
};

const selectionIsIn = (cell) => {
  const selection = window.getSelection();
  if (!selection || !selection.anchorNode) return false;
  const node = selection.anchorNode;
  return cell === node || cell.contains(node);
};

const applyInlineFormat = (cell, tag) => {
  const command = tag === "em" ? "italic" : "bold";
  cell.focus();
  const selection = window.getSelection();
  if (!selectionIsIn(cell) || selection?.isCollapsed) {
    const range = document.createRange();
    range.selectNodeContents(cell);
    selection.removeAllRanges();
    selection.addRange(range);
  }
  document.execCommand(command);
  renameInlineTags(cell, "b", "strong");
  renameInlineTags(cell, "i", "em");
  return cell.innerHTML;
};

const TableCell = ({ html, onChange, onActivate }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== html) {
      ref.current.innerHTML = html;
    }
  }, [html]);

  const activate = () => {
    if (ref.current) onActivate(ref.current);
  };

  return (
    <div
      ref={ref}
      className="table-control__cell"
      contentEditable
      suppressContentEditableWarning
      onFocus={activate}
      onMouseUp={activate}
      onKeyUp={activate}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        document.execCommand("insertHTML", false, "<br>");
        if (ref.current) onChange(ref.current.innerHTML);
      }}
      onInput={(event) => onChange(event.currentTarget.innerHTML)}
    />
  );
};

const TableControl = ({ value = {}, onChange, blockId }) => {
  const table = parseTableHtml(value.content);
  const columnCount = Math.max(table.headers.length, 1);
  const rows = [table.headers, ...table.rows];
  const selectionRef = useRef(null);

  const commit = (next) => {
    onChange({
      ...value,
      content: serializeTableHtml(next),
      style: value.style && typeof value.style === "object" ? value.style : {},
    });
  };

  const updateCell = (rowIndex, cellIndex, content) => {
    if (rowIndex === 0) {
      commit({
        ...table,
        headers: table.headers.map((cell, index) => (index === cellIndex ? content : cell)),
      });
      return;
    }
    commit({
      ...table,
      rows: table.rows.map((row, currentRow) =>
        currentRow === rowIndex - 1
          ? row.map((cell, currentCell) => (currentCell === cellIndex ? content : cell))
          : row
      ),
    });
  };

  useEffect(() => {
    if (!blockId) return undefined;
    tableFormatters.set(blockId, (tag) => {
      const selection = selectionRef.current;
      if (!selection?.cell) return;
      const nextHtml = applyInlineFormat(selection.cell, tag);
      updateCell(selection.rowIndex, selection.cellIndex, nextHtml);
    });
    return () => tableFormatters.delete(blockId);
  });

  const addColumn = () => {
    commit({
      headers: [...table.headers, ""],
      rows: table.rows.map((row) => [...row, ""]),
    });
  };

  const removeColumn = () => {
    if (columnCount <= 1) return;
    commit({
      headers: table.headers.slice(0, -1),
      rows: table.rows.map((row) => row.slice(0, -1)),
    });
  };

  const addRow = () => {
    commit({
      ...table,
      rows: [...table.rows, Array.from({ length: columnCount }, () => "")],
    });
  };

  const removeRow = () => {
    if (rows.length <= 1) return;
    if (table.rows.length <= 1) {
      commit({
        headers: table.headers,
        rows: [],
      });
      return;
    }
    commit({
      ...table,
      rows: table.rows.slice(0, -1),
    });
  };

  return (
    <div className="table-control">
      <div className="table-control__scroll">
        <table className="table-control__table">
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`cell-${rowIndex}-${cellIndex}`}>
                    <TableCell
                      html={cell}
                      onActivate={(element) => {
                        selectionRef.current = { rowIndex, cellIndex, cell: element };
                      }}
                      onChange={(content) => updateCell(rowIndex, cellIndex, content)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="table-control__actions">
        <button type="button" onClick={addRow}>
          <Icon icon="ph:plus" height={12} /> Row
        </button>
        <button type="button" onClick={removeRow} disabled={rows.length <= 1}>
          <Icon icon="ph:minus" height={12} /> Row
        </button>
        <button type="button" onClick={addColumn}>
          <Icon icon="ph:plus" height={12} /> Column
        </button>
        <button type="button" onClick={removeColumn} disabled={columnCount <= 1}>
          <Icon icon="ph:minus" height={12} /> Column
        </button>
      </div>
    </div>
  );
};

export default TableControl;
